import React, { useEffect, useState } from "react";
import { Bell, BellSlash, Check, CloudArrowUp, X, SunHorizon, MoonStars } from "@phosphor-icons/react";
import { isPushSupported, getDailyBriefing, setDailyBriefing, getQuietHours, setQuietHours } from "../../lib/pushClient";

export function PushNotificationsPanel({
  pushEnabled,
  pushBusy,
  pushMsg,
  onToggle,
  onTest,
}) {
  if (!isPushSupported()) return null;
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tac-text">
            {pushEnabled ? (
              <Bell size={12} weight="fill" className="text-tac-primary" />
            ) : (
              <BellSlash size={12} weight="bold" className="text-tac-dim" />
            )}
            Proximity Push Alerts
          </div>
          <div className="text-[15px] font-mono-tac text-tac-faint mt-0.5">
            {pushEnabled ? "Active on this device" : "Notify me when fires enter my radius"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {pushEnabled && (
            <button
              data-testid="test-push-btn"
              onClick={onTest}
              disabled={pushBusy}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text rounded-sm disabled:opacity-40"
            >
              Test
            </button>
          )}
          <button
            data-testid="toggle-push-btn"
            onClick={onToggle}
            disabled={pushBusy}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-40 ${
              pushEnabled
                ? "border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text"
                : "bg-tac-primary text-white hover:bg-tac-primary/90"
            }`}
          >
            {pushBusy ? "..." : pushEnabled ? "Turn off" : "Enable"}
          </button>
        </div>
      </div>
      {pushEnabled && <DailyBriefingToggle />}
      {pushEnabled && <QuietHoursToggle />}
      {pushMsg && (
        <div
          data-testid="push-msg"
          className={`text-[11px] ${
            pushMsg.tone === "error"
              ? "text-tac-danger"
              : pushMsg.tone === "success"
              ? "text-tac-primary"
              : "text-tac-dim"
          }`}
        >
          {pushMsg.text}
        </div>
      )}
    </>
  );
}

function DailyBriefingToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDailyBriefing().then(setEnabled).catch(() => {});
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !enabled;
      await setDailyBriefing(next);
      setEnabled(next);
    } catch (e) {
      console.warn("daily briefing toggle failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tac-text">
          <SunHorizon size={12} weight={enabled ? "fill" : "bold"} className={enabled ? "text-tac-primary" : "text-tac-dim"} />
          Daily Briefings (8 AM &amp; 8 PM)
        </div>
        <div className="text-[15px] font-mono-tac text-tac-faint mt-0.5">
          {enabled ? "Morning briefing + evening recap enabled" : "Morning fire summary & evening recap"}
        </div>
      </div>
      <button
        data-testid="toggle-daily-briefing-btn"
        onClick={toggle}
        disabled={busy}
        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-40 shrink-0 ${
          enabled
            ? "border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text"
            : "bg-tac-primary text-white hover:bg-tac-primary/90"
        }`}
      >
        {busy ? "..." : enabled ? "Turn off" : "Enable"}
      </button>
    </div>
  );
}

const HOUR_OPTS = Array.from({ length: 24 }, (_, h) => ({
  value: h * 60,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`,
}));

function QuietHoursToggle() {
  const [cfg, setCfg] = useState({ enabled: false, start_min: 1320, end_min: 420 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getQuietHours().then(setCfg).catch(() => {});
  }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      await setQuietHours(next);
      setCfg(next);
    } catch (e) {
      console.warn("quiet hours save failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tac-text">
            <MoonStars size={12} weight={cfg.enabled ? "fill" : "bold"} className={cfg.enabled ? "text-tac-primary" : "text-tac-dim"} />
            Quiet Hours
          </div>
          <div className="text-[15px] font-mono-tac text-tac-faint mt-0.5">
            {cfg.enabled
              ? "Non-emergency alerts muted overnight"
              : "Mute non-emergency alerts overnight"}
          </div>
        </div>
        <button
          data-testid="toggle-quiet-hours-btn"
          onClick={() => save({ ...cfg, enabled: !cfg.enabled })}
          disabled={busy}
          className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-40 shrink-0 ${
            cfg.enabled
              ? "border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-text"
              : "bg-tac-primary text-white hover:bg-tac-primary/90"
          }`}
        >
          {busy ? "..." : cfg.enabled ? "Turn off" : "Enable"}
        </button>
      </div>
      {cfg.enabled && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-tac-dim">
          <span>From</span>
          <select
            data-testid="quiet-hours-start"
            value={cfg.start_min}
            disabled={busy}
            onChange={(e) => save({ ...cfg, start_min: Number(e.target.value) })}
            className="bg-tac-surface border border-tac-border rounded-sm px-1.5 py-1 text-[11px] text-tac-text"
          >
            {HOUR_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span>to</span>
          <select
            data-testid="quiet-hours-end"
            value={cfg.end_min}
            disabled={busy}
            onChange={(e) => save({ ...cfg, end_min: Number(e.target.value) })}
            className="bg-tac-surface border border-tac-border rounded-sm px-1.5 py-1 text-[11px] text-tac-text"
          >
            {HOUR_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="mt-1.5 text-[10px] text-tac-faint leading-snug">
        Level 3 GO evacuations and official flood warnings always break through.
      </div>
    </div>
  );
}

export function SyncIndicator({ syncState }) {
  if (syncState.status === "idle") return null;
  const cls =
    syncState.status === "synced"
      ? "text-tac-primary"
      : syncState.status === "syncing"
      ? "text-tac-info"
      : "text-tac-danger";
  return (
    <span
      data-testid="sync-indicator"
      className={`inline-flex items-center gap-1 uppercase tracking-wider font-bold ${cls}`}
    >
      {syncState.status === "synced" && (
        <>
          <Check size={9} weight="bold" /> Synced to your account
        </>
      )}
      {syncState.status === "syncing" && (
        <>
          <CloudArrowUp size={9} weight="bold" className="animate-pulse" /> Syncing…
        </>
      )}
      {syncState.status === "error" && (
        <>
          <X size={9} weight="bold" /> Sync error
        </>
      )}
    </span>
  );
}
